import AdminTabsContext from 'components/shared/edit/tabs/AdminTabsContext';
import React, { useContext, useEffect } from 'react';
import { FieldRenderProps } from 'react-final-form';
import { FormFeedback } from 'reactstrap';

type ValidationMessagesProps = Pick<FieldRenderProps<any, HTMLInputElement>, 'meta'> & {
	id: string;

	/**
	 * Option dataId to be used for the data-testId. If not provided the data-testId will default to "id + '-validation-msg'"
	 */
	dataId?: string;
};

/**
 * Displays validation errors for fields
 *
 * @class ValidationMessages
 * @extends {React.Component<ValidationMessagesProps>}
 */

function ValidationMessages(props: ValidationMessagesProps) {
	const { meta, id } = props;
	const { setValidationError, parentTab } = useContext(AdminTabsContext);

	useEffect(() => {
		if (!setValidationError) {
			// setValidationError may not be set if we're not within a tab context.
			return;
		}

		// if there's a validation error and there's some tab context (there won't be if you're not using tabs)
		if (meta.dirtySinceLastSubmit) {
			setValidationError(parentTab, id);
		} else if (meta.submitError && meta.touched) {
			setValidationError(parentTab, id, meta.submitError);
		}
	}, [meta.submitError, meta.touched, meta.dirtySinceLastSubmit, parentTab, id, setValidationError]);

	if (!meta) {
		return null;
	}

	let errors: string[] = [];

	/*
	 all validation comes from the server, so we only need to check `submitError` despite there being `error`
	 there are 3 kinds of values for submitError, string, string[] and [string|[]|object]
	 the third type happens when you're using FieldArray, so we're going to filter those out and only
	 take the strings if submitError is an array. this is due to a bug (possibly feature?) of final form
	 where the submit errors for field array fields bubble up into the parent field.
	 */
	if (meta.submitError) {
		errors = errors.concat(getErrorArray(meta.submitError));

		// for server-side validation, we need to stop showing these after any changes are made.
		if (meta.dirtySinceLastSubmit || meta.submitting) {
			return null;
		}
	} else if (meta.error) {
		// sometimes we validate on the client, so we need to support these as well.
		errors = errors.concat(getErrorArray(meta.error));
	}

	const getDataTestId = () => {
		if (props.dataId !== undefined && props.dataId !== null) {
			return props.dataId;
		}
		return props.id + '-validation-msg';
	};

	return (
		<FormFeedback id={props.id + '-feedback'} data-testid={getDataTestId()}>
			{(meta.submitError || meta.error) &&
				errors.length > 0 &&
				errors.map((error, index) => (
					<React.Fragment key={index}>
						<span>{error}</span>
						<br />
					</React.Fragment>
				))}
		</FormFeedback>
	);
}

/**
 * Filters and return an array of error depending on the type on input
 * @param errorOrSubmitError
 */
export function getErrorArray(errorOrSubmitError) {
	let errors: string[] = [];
	if (typeof errorOrSubmitError === 'string') {
		errors.push(errorOrSubmitError);
	} else if (Array.isArray(errorOrSubmitError)) {
		errors = errorOrSubmitError.filter((error) => !Array.isArray(error) && typeof error === 'string');
	}

	return errors;
}

export default ValidationMessages;
